import React, { useState } from 'react';
import {
    NativeSyntheticEvent,
    TextInputKeyPressEventData,
} from 'react-native';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import Toast from 'react-native-toast-message';
import FilterInput from '../../../components/Filter/components/FilterInput';
import Filter from './Filters';
import UnitDisplacedReasonModal from './UnitDisplacedReasonModal';
import DeleteModal from '../../../components/DeleteModal';
import MyDataTable from '../../../components/MyDataTable';
import { Pagination } from '../../../components/Pagination';
import CustomButton from '../../../components/CustomButton';
import { translate } from '../../../services/translate';
import { dispatchService } from '../../../services/central-api/dispatch';
import { ClientError } from '../../../services/central-api/base-service';
import { DispatchParamList } from '../../../typings/Params';
import useFilters from '../../../components/Filter/hooks/useFilters';
import { useAuth } from '../../../context/Auth';
import DropdownButton from '../../../components/DropdownButton';
import { useQuery } from '@tanstack/react-query';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';

export default function UnitDisplacedReasons({ navigation }: { navigation: Navigation; }) {
    const route = useRoute<RouteProp<DispatchParamList, 'UnitDisplacedReasons'>>();
    const { user: authenticatedUser } = useAuth();

    const [filters, setFilters] = useFilters<DispatchParamList['UnitDisplacedReasons']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');

    const [modalVisible, setModalVisible] = useState(false);
    const [selectedUnitDisplacedReason, setUnitDisplacedReasonSelected] = useState<UnitDisplacedReason>();
    const [deleteVisible, setDeleteVisible] = useState(false);

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['getPaginatedUnitDisplacedReasons', filters],
        queryFn: () => dispatchService.getPaginatedUnitDisplacedReasons(filters),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    async function confirmDelete(reason: UnitDisplacedReason) {
        try {
            await dispatchService.deleteUnitDisplacedReason(reason.id);
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully')
            });
            setDeleteVisible(false);
            refetch();
        } catch (error) {
            if (error instanceof ClientError) {
                Toast.show({
                    type: 'sentinelxError',
                    text1: translate('unexpectedError'),
                });
            }
        }
    }
    function handleFormClose() {
        setModalVisible(false);
        setUnitDisplacedReasonSelected(undefined);
        refetch();
    }
    function onKey(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key === 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
            setFilters(f => ({
                ...f,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    return (
        <Container>
            <Header
                title={translate('unitDisplacedReasons')}
                actions={
                    <CustomButton
                        text={translate('register')}
                        onPress={() => {
                            setUnitDisplacedReasonSelected(undefined);
                            setModalVisible(true);
                        }}
                    />
                }
            />
            <Card>
                <DataTableHeader
                    zIndex={2}
                    title={translate('registeredUnitDisplacedReasons')}
                    filter={
                        <Filter
                            filters={filters}
                            setFilters={setFilters}
                            textFilter={textFilter}
                            setTextFilter={setTextFilter}
                        />
                    }
                    filterInput={
                        <FilterInput
                            value={textFilter}
                            placeholder={translate('typeToSearch')}
                            onChange={setTextFilter}
                            onKeyPress={onKey}
                        />
                    }
                />

                <DataTableContainer
                    dataTable={
                        <MyDataTable
                            columns={[
                                {
                                    name: 'ID',
                                    selector: r => r.id,
                                    grow: 0
                                },
                                {
                                    name: translate('name'),
                                    selector: r => r.name,
                                    grow: 2,
                                    wrap: true
                                },
                                {
                                    name: translate('actingBody'),
                                    selector: row => row.actingBody?.name || '',
                                    wrap: true,
                                },
                                {
                                    cell: row => (
                                        <DropdownButton
                                            actions={[
                                                {
                                                    content: translate('editInformation'),
                                                    onPress: () => {
                                                        setUnitDisplacedReasonSelected(row);
                                                        setModalVisible(true);
                                                    }
                                                },
                                                {
                                                    content: translate('delete'),
                                                    variant: 'danger',
                                                    onPress: () => {
                                                        setUnitDisplacedReasonSelected(row);
                                                        setDeleteVisible(true);
                                                    },
                                                }
                                            ]}
                                        />
                                    ),
                                    wrap: true,
                                    button: true,
                                },
                            ]}
                            data={data?.rows ?? []}
                            progressPending={isLoading}
                        />
                    }
                    pagination={
                        <Pagination
                            currentPage={filters.page}
                            totalItems={data?.count ?? 0}
                            pageSize={filters.limit}
                            setPage={p => {
                                setFilters(f => ({ ...f, page: p }));
                                navigation.setParams({ ...route.params, page: p + 1 });
                            }}
                            setPageSize={s => {
                                setFilters(f => ({ ...f, limit: s, page: 0 }));
                                navigation.setParams({ ...route.params, limit: s, page: 1 });
                            }}
                        />
                    }
                />
            </Card>

            <UnitDisplacedReasonModal
                unitDisplacedReasonId={selectedUnitDisplacedReason?.id}
                onClose={handleFormClose}
                isModalVisible={modalVisible}
                setModalVisible={setModalVisible}
                authenticatedUser={authenticatedUser}
            />

            {selectedUnitDisplacedReason && (
                <DeleteModal
                    isModalVisible={deleteVisible}
                    setModalVisible={setDeleteVisible}
                    itemName={selectedUnitDisplacedReason.name}
                    onSubmit={async () => {
                        await confirmDelete(selectedUnitDisplacedReason);
                    }}
                />
            )}
        </Container>
    );
}
